import { CardContent, CardHeader, FormControl, FormControlLabel, FormHelperText, Grid2, IconButton, Stack, Switch } from "@mui/material"
import DashboardLayout from "../../../layoutcontainers/DashboardLayout"
import MaterialTypography from "../../../components/MaterialTypography"
import MaterialInput from "../../../components/MaterialInput"
import MaterialSelect from "../../../components/MasterSelect"
import { useFieldMappingFormHandling } from "./components/FieldMappingStateHandle";
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSnackbar } from "../../../context/popup"
import { useLoading } from "../../../components/Loading"
import MaterialBox from "../../../components/MaterialBox"
import MaterialButton from "../../../components/MaterialButton"
import { ArrowBack } from "@mui/icons-material"
import { fetchData, fetchDataById, postData, putData } from "../../../services"
import { useEffect, useState } from "react"
import DropDownConfig from "../../Utils/DropDownConfig"

const FieldMappingForm = () => {

    const [searchParams] = useSearchParams();
    const fieldId = searchParams.get("id");
    const pageType = searchParams.get("pageType");
    const pageMode = pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { setCircleLoading } = useLoading();

    const [fieldOptions, setFieldOptions] = useState([]);
    const [insurerRegistryOptions, setInsurerRegistryOptions] = useState([]);
    const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
    const [dataDirectionOptions, setDataDirectionOptions] = useState([]);
    const [transformFunctionOptions, setTransformFunctionOptions] = useState([]);

    useEffect(() => {
        if(pageMode === "view"){
            return
        }
        fetchAllDropdowns();
    }, [pageMode]);

    //get data by id
    useEffect(() => {
        if((pageMode === "add") || (!fieldId)){
            return
        }
        const fetchFieldMappingDataById = async (fieldId) => {
            try {
            setCircleLoading(true);
            const response = await fetchDataById(`/api-engine/field-mapping/${fieldId}`);
            const responseData = response?.data;
            
            if(response?.success){
                setFormData(prev => ({
                ...prev,
                //field details
                version: responseData.version || "",
                isActive: responseData?.isActive,                
                externalFieldPath: responseData?.externalFieldPath || "",
                mappingConfig: JSON.stringify(responseData?.mappingConfig) || "",
                //dropdown                   
                fieldId: {value: responseData?.fieldDictionary?.id, label: responseData?.fieldDictionary?.label},
                iRegistryId: {value: responseData?.insurerRegistry?.id, label: responseData?.insurerRegistry?.id},
                apiCategoryId: {value: responseData?.apiCategory?.id, label: responseData?.apiCategory?.keyName},
                dataDirectionId: {value: responseData?.dataDirection?.id, label: responseData?.dataDirection?.keyName},            
                transformFnId:  {value: responseData?.transformFnId, label: responseData?.transformFnId}, 

                // reset status fields
                versionStatus: responseData?.version ? "success" : "unchecked",
                externalFieldPathStatus: responseData?.externalFieldPath ? "success" : "unchecked",
                mappingConfigStatus: responseData?.mappingConfig ? "success" : "unchecked",                
                fieldIdStatus: responseData?.fieldDictionary?.id ? "success" : "unchecked",
                iRegistryIdStatus: responseData?.insurerRegistry?.id ? "success" : "unchecked",
                apiCategoryIdStatus: responseData?.apiCategory?.id ? "success" : "unchecked",                
                dataDirectionIdStatus: responseData?.dataDirection?.id ? "success" : "unchecked",
                transformFnIdStatus: responseData?.transformFnId ? "success" : "unchecked", 
                }));
            }
            else{
                //throw error message
            }
            } catch (error) {
            console.error('Error fetching data by ID:', error);
            showSnackbar(error?.response?.data?.message || "Network Error","error");
            }finally{
                setCircleLoading(false);
            }
        };
        fetchFieldMappingDataById(fieldId);
    },[pageMode]);

    //all dropdown api function
    const fetchAllDropdowns = async () => {
        setCircleLoading(true);

        try {
            const [
            fieldResponse,
            insurerRegistryResponse,
            apiCategoryResponse,
            dataDirectionResponse,
            transformFunctionResponse
            ] = await Promise.allSettled([
            fetchData(`/api-engine/field-dictionary?dropdown=true&isActive=true`),
            fetchData(`/reference/api-engine/insurer-registry?dropdown=true&isActive=true`),
            fetchData(`/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiApiCategory}`), 
            fetchData(`/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiDataDirection}`),
            fetchData(`/api-engine/transform-dictionary?dropdown=true&isActive=true`)
            ]);

            const formattedFields = fieldResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.label,
            })) || [];
            
            const formattedInsurerRegistry = insurerRegistryResponse?.value?.data?.insurer_registries?.map((item) => ({
                value: item?.id,
                label: item?.gInsurerName,
            })) || [];

            const formattedApiCategories = apiCategoryResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.keyName,
            })) || [];

            const formattedDataDirections = dataDirectionResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.keyName,
            })) || [];

            const formattedTransformFunctions = transformFunctionResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.name,
            })) || [];

            
            setFieldOptions(formattedFields);
            setInsurerRegistryOptions(formattedInsurerRegistry);
            setApiCategoryOptions(formattedApiCategories);
            setDataDirectionOptions(formattedDataDirections);
            setTransformFunctionOptions(formattedTransformFunctions);

        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            showSnackbar("Error fetching some of the Dropdowns", "warning");
        } finally {
            setCircleLoading(false);
        }
    };

    const handleSubmitFieldMapping = async () => {
        const payload = {
            fieldId: formData?.fieldId?.value,
            iRegistryId: formData?.iRegistryId?.value,
            apiCategoryId: formData?.apiCategoryId?.value,
            mappingConfig: formData?.mappingConfig,
            dataDirectionId: formData?.dataDirectionId?.value,
            externalFieldPath: formData?.externalFieldPath,
            transformFnId: formData?.transformFnId?.value,
            version: formData?.version,
            isActive: formData?.isActive
        };
        //Update
        if(fieldId){
            try {
                setCircleLoading(true);
                const response = await putData(`/api-engine/field-mapping/${fieldId}`, payload);
                
                const responseMessage = response?.message;
                if(response?.success){
                //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/api-integration-engine/field-mapping');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error")
                }
            } catch (error) {
                console.error('Error updating data:', error);
                showSnackbar(error?.response?.data?.message || "Error Updating","error");
            }finally{
                setCircleLoading(false);
            }
        }
        else{      
        //Submit
            try {
                setCircleLoading(true);
                const response = await postData('/api-engine/field-mapping', payload);
                
                const responseMessage = response?.message;
                if(response?.success){
                    //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/api-integration-engine/field-mapping');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error");
                }
            } catch (error) {
                console.error('Error posting data:', error);
                //catch error message
                showSnackbar(error?.response?.data?.message || "Network Error","error")
            }finally{
                setCircleLoading(false);
            }
        }
    };
    const { formData, setFormData, handleChange, checkAllFields } = useFieldMappingFormHandling(); 
  return (
    <DashboardLayout>
        <CardHeader
        title={(
            <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => navigate("/api-integration-engine/field-mapping")}>
                        <ArrowBack />
                    </IconButton>
                    <Stack direction="column" spacing={0}>
                    <MaterialTypography variant="h5" fontWeight="medium">
                        Field Mapping Details
                    </MaterialTypography>
                    <MaterialTypography variant="caption" color="text">
                        Enter New Field Mapping Details according to Field Dictionary.
                    </MaterialTypography>
                    </Stack>
                </Stack>
            </Stack>
        )}
        />
        <CardContent sx={{ minHeight: "65vh" }}>
            <Grid2 container spacing={1}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <MaterialTypography variant="caption" fontWeight="bold">
                        Field*
                    </MaterialTypography>
                    <MaterialSelect
                        name="fieldId"
                        value={formData.fieldId}
                        options={fieldOptions}
                        onChange={handleChange}
                        success={formData.fieldIdStatus === "success"}
                        error={formData.fieldIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <MaterialTypography variant="caption" fontWeight="bold">
                        Insurer Registry*
                    </MaterialTypography>
                    <MaterialSelect
                        name="iRegistryId"
                        value={formData.iRegistryId}
                        options={insurerRegistryOptions}
                        onChange={handleChange}
                        success={formData.iRegistryIdStatus === "success"}
                        error={formData.iRegistryIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <MaterialTypography variant="caption" fontWeight="bold">
                        API Category* 
                    </MaterialTypography>
                    <MaterialSelect
                        name="apiCategoryId"
                        value={formData.apiCategoryId}
                        options={apiCategoryOptions}
                        onChange={handleChange}
                        success={formData.apiCategoryIdStatus === "success"}
                        error={formData.apiCategoryIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                    />
                </Grid2>                
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <MaterialTypography variant="caption" fontWeight="bold">
                       Data Direction*
                    </MaterialTypography>
                    <MaterialSelect
                        name="dataDirectionId"
                        value={formData.dataDirectionId}
                        options={dataDirectionOptions}
                        onChange={handleChange}
                        success={formData.dataDirectionIdStatus === "success"}
                        error={formData.dataDirectionIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                    />
                </Grid2>       
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    External Field Path*
                  </MaterialTypography>
                <MaterialInput
                  name="externalFieldPath"
                  placeholder="eg. Business Type"
                  value={formData.externalFieldPath}
                  onChange={handleChange}
                  error={formData.externalFieldPathStatus === "error" ? true : false}
                  success={formData.externalFieldPathStatus === "success" ? true : false}
                  disabled={pageMode === "view"}
                />
                {formData.externalFieldPathStatus === "error" && (
                  <FormControl error>
                    <FormHelperText>
                      External Field Path should be maximum 20 characters long and can contain
                      only alphabets
                    </FormHelperText>
                  </FormControl>
                )}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <MaterialTypography variant="caption" fontWeight="bold">
                       Transform Function Id*
                    </MaterialTypography>
                    <MaterialSelect
                        name="transformFnId"
                        value={formData.transformFnId}
                        options={transformFunctionOptions}
                        onChange={handleChange}
                        success={formData.transformFnIdStatus === "success"}
                        error={formData.transformFnIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Mapping Configuration*
                  </MaterialTypography>
                <MaterialInput
                name="mappingConfig"
                placeholder={`eg. { "mappingRules" : 
        { "source": "customer.name", "transform": "to_upper" } 
    }`}
                value={formData.mappingConfig}
                onChange={handleChange}
                error={formData.mappingConfigStatus === "error" ? true : false}
                success={formData.mappingConfigStatus === "success" ? true : false}
                multiline
                rows={3}
                disabled={pageMode === "view"}
                />
                {formData.mappingConfigStatus === "error" && (
                  <FormControl error>
                    <FormHelperText>
                      Mapping Configuration should be maximum 20 characters long and can contain
                      only alphabets
                    </FormHelperText>
                  </FormControl>
                )}
                </Grid2> 
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <MaterialTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                    >
                        Version
                    </MaterialTypography>
                    <MaterialInput
                    name="version"
                    placeholder="eg. 1.0.0"
                    value={formData.version}
                    onChange={handleChange}
                    error={formData.versionStatus === "error" ? true : false}
                    success={formData.versionStatus === "success" ? true : false}
                    disabled={pageMode === "view"}
                    />
                {formData.versionStatus === "error" && (
                  <FormControl error>
                    <FormHelperText>
                      Version should be maximum 20 characters long
                    </FormHelperText>
                  </FormControl>
                )}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MaterialTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                    >
                        Active
                    </MaterialTypography>
                    <FormControlLabel
                    control={
                        <Switch
                        checked={formData.isActive}
                        onChange={(e) =>
                            handleChange({
                            target: {
                                name: "isActive",
                                value: e.target.checked,
                            },
                            })
                        }
                        disabled={pageMode === "view"}
                        color="primary"
                        />
                    }
                    label={
                        <MaterialTypography variant="caption" fontWeight="bold">
                        {formData.isActive ? "Yes" : "No"}
                        </MaterialTypography>
                    }
                    sx={{ marginLeft: 0 }}
                    />
                </Grid2>
            </Grid2>
        </CardContent>
        <MaterialBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        >
            {pageMode !== "view" && (
            <>
                <MaterialButton
                sx={{ m: 2 }}
                variant="outlined"
                color="dark"
                onClick={() => navigate("/insurer-master/insurer-addon")}
                >
                Cancel
                </MaterialButton>
                <MaterialButton
                sx={{ m: 2 }}
                variant="gradient"
                color="dark"
                disabled={!checkAllFields()}
                onClick={handleSubmitFieldMapping}
                >
                {pageMode === "edit" ? "Update" : "Submit"}
                </MaterialButton>
            </>
            )}
        </MaterialBox>
    </DashboardLayout>
  )
}

export default FieldMappingForm